import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import { ORG_ID, FOUNDER_ID } from "../data/schema";
import { SITE, canonicalFor } from "../lib/site";
import { Reveal, Chapter } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { POSTS } from "../data/posts";
import { BLOG_SEO_TITLES, BLOG_SEO_DESCRIPTIONS } from "../data/routeSeo";
import { SERVICES } from "../data/content";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";

// The service page most relevant to an article, by topic.
const relatedServiceSlug = (post) => {
  const t = `${post.slug} ${post.title}`.toLowerCase();
  if (t.includes("shopify")) return "shopify-website-development";
  if (t.includes("chatbot")) return "ai-chatbot-development";
  if (t.includes("automat")) return "ai-business-automation";
  if (t.includes("local-seo") || t.includes("near me")) return "local-seo-websites";
  if (t.includes("seo")) return "seo-services";
  if (t.includes("redesign")) return "website-redesign";
  if (t.includes("b2b") || t.includes("manufactur")) return "b2b-lead-generation-websites";
  if (t.includes("lead") || t.includes("enquir") || t.includes("conversion")) return "lead-generation-websites";
  if (t.includes("design")) return "website-design";
  return "website-development";
};

const MidCta = () => (
  <div className="my-14 rounded-3xl bg-ember p-8 text-center sm:p-10" data-testid="blog-mid-cta">
    <p className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
      Want a Website That Generates Leads?
    </p>
    <p className="mx-auto mt-2 max-w-md text-sm text-white/85">
      Get a free website plan built around your business and your customers.
    </p>
    <button
      data-testid="blog-mid-cta-btn"
      onClick={() => { track("blog_mid_cta_click", {}); openLeadPopup(); }}
      className="mt-6 rounded-full bg-white px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-obsidian transition-transform hover:scale-105"
    >
      Get a Free Website Plan
    </button>
  </div>
);

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog/" replace />;

  const mid = Math.ceil(post.sections.length / 2);
  // Related reading: same category first, then the newest of the rest.
  const others = [
    ...POSTS.filter((p) => p.slug !== slug && p.category === post.category),
    ...POSTS.filter((p) => p.slug !== slug && p.category !== post.category),
  ].slice(0, 4);
  const service = SERVICES.find((s) => s.slug === relatedServiceSlug(post));
  const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title={BLOG_SEO_TITLES[post.slug] || post.title}
        description={BLOG_SEO_DESCRIPTIONS[post.slug] || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        schema={{
          "@type": "BlogPosting",
          "@id": `${canonicalFor(`/blog/${post.slug}`)}#article`,
          headline: post.title,
          description: post.excerpt,
          image: `${SITE}/og-image.png`,
          datePublished: post.date,
          dateModified: post.updated || post.date,
          author: { "@id": FOUNDER_ID },
          publisher: { "@id": ORG_ID },
          mainEntityOfPage: { "@id": `${canonicalFor(`/blog/${post.slug}`)}#webpage` },
          inLanguage: "en",
        }}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Insights", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]}
      />
      <article className="mx-auto max-w-3xl px-4 sm:px-8" data-testid="blog-post">
        <Link
          to="/blog/"
          data-testid="blog-back-link"
          className="flex w-fit items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All Insights
        </Link>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember">{post.category}</span>
          <span className="h-1 w-1 rounded-full bg-white/25" />
          <span className="font-mono text-[11px] text-white/35">
            <time dateTime={post.date}>{fmt(post.date)}</time> · Updated <time dateTime={post.updated}>{fmt(post.updated)}</time> · {post.readTime}
          </span>
        </div>
        <h1 className="mt-6 font-display text-3xl font-black leading-[1.1] tracking-tight sm:text-5xl">
          {post.title}
        </h1>
        <p className="mt-5 text-sm text-white/50" data-testid="blog-byline">
          By{" "}
          <Link to="/about/" className="font-semibold text-white/80 underline-offset-2 hover:text-ember hover:underline">
            Somil Grover
          </Link>
          , founder of Grotech Web
        </p>

        <div className="mt-12">
          {post.sections.map((s, i) => (
            <section key={i}>
              {i === mid && <MidCta />}
              {s.h2 && (
                <h2 className="mt-12 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  {s.h2}
                </h2>
              )}
              {s.paragraphs?.map((p, j) => (
                <p key={j} className="mt-5 text-base leading-relaxed text-white/65">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-5 space-y-3">
                  {s.list.map((li) => (
                    <li key={li} className="flex items-start gap-3 text-base leading-relaxed text-white/65">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ember" />
                      {li}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {service && (
          <p className="mt-14 rounded-2xl border border-ember/30 bg-ember/5 p-6 text-sm text-white/70" data-testid="blog-related-service">
            Related service:{" "}
            <Link to={`/${service.slug}/`} className="font-semibold text-ember underline-offset-2 hover:underline">
              {service.heroTitle}
            </Link>
            {" "}·{" "}
            <Link to="/pricing/" className="font-semibold text-white underline-offset-2 hover:text-ember hover:underline">
              See pricing
            </Link>
          </p>
        )}

        <div className="my-16 rounded-3xl border border-white/10 bg-ink p-8 sm:p-10" data-testid="blog-end-cta">
          <Chapter index="→" label="Next Step" />
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl">
            Ready to Turn Your Website Into a Lead Generation Tool?
          </h2>
          <p className="mt-3 max-w-md text-sm text-white/50">
            Tell me about your business — get a website strategy built around enquiries, not just looks.
          </p>
          <div className="mt-7 flex flex-wrap gap-4">
            <button
              data-testid="blog-end-discuss-btn"
              onClick={() => { track("blog_end_cta_click", { post: slug }); openLeadPopup(); }}
              className="group flex items-center gap-2 rounded-full bg-ember px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Discuss My Business <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={waLink(CONTEXT_MESSAGES.blog)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-end-whatsapp-btn"
              onClick={() => track("whatsapp_click", { location: "blog_end", post: slug })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
          <p className="mt-5 text-xs text-white/35">
            <Link to="/reviews/" className="underline-offset-2 hover:text-ember hover:underline">
              Read Grotech Web reviews from clients in India & the USA
            </Link>
          </p>
        </div>

        <div className="mb-24 border-t border-white/10 pt-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">Keep Reading</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}/`}
                data-testid={`blog-related-${p.slug}`}
                className="group rounded-2xl border border-white/10 bg-ink p-6 transition-colors hover:border-ember/40"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">{p.category}</p>
                <p className="mt-2 font-display text-base font-bold leading-snug tracking-tight group-hover:text-ember">
                  {p.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </main>
  );
}
